const POLL_INTERVAL_MS = 5000;
const TOTAL_TIMEOUT_MS = 5 * 60 * 1000;
const MAX_CONSECUTIVE_ERRORS = 5;
const CREEP_START = 12;
const CREEP_STEP = 6;
const CREEP_CAP = 90;

const SPINNER_HTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
const ICON_SUCCESS = '<i class="fal fa-check-circle"></i>';
const ICON_ERROR = '<i class="fal fa-exclamation-circle"></i>';
const ICON_WARNING = '<i class="fal fa-exclamation-triangle"></i>';

let activeToken = 0;

interface StatusBody {
  status: string;
  error_message?: string | null;
  campaign_id?: string | null;
  facebook_adset_id?: string | null;
}

interface ProgressUI {
  $wrap: JQuery;
  $fill: JQuery;
  $icon: JQuery;
  $label: JQuery;
  $percent: JQuery;
}

export function startAdSetCreateProgress(adSetId: string): void {
  const token = ++activeToken;
  const $wrap = $('#adset-create-progress');
  if (!$wrap.length) return;

  const ui: ProgressUI = {
    $wrap,
    $fill: $wrap.find('[data-role="ccp-fill"]'),
    $icon: $wrap.find('[data-role="ccp-icon"]'),
    $label: $wrap.find('[data-role="ccp-label"]'),
    $percent: $wrap.find('[data-role="ccp-percent"]'),
  };

  $wrap.removeClass('d-none is-success is-error is-warning').addClass('is-active');
  ui.$fill.addClass('progress-bar-striped progress-bar-animated');
  ui.$icon.html(SPINNER_HTML);
  ui.$label.text('Processing on Meta…').removeAttr('title');
  setBar(ui, CREEP_START, true);

  $wrap.find('[data-role="ccp-dismiss"]').off('click.ccp').on('click.ccp', function() {
    activeToken++;
    hideProgress($wrap);
  });

  void runPoll(adSetId, token, ui);
}

async function runPoll(adSetId: string, token: number, ui: ProgressUI): Promise<void> {
  const deadline = Date.now() + TOTAL_TIMEOUT_MS;
  let consecutiveErrors = 0;
  let lastStatus: string | null = null;
  let creep = CREEP_START;

  while (Date.now() < deadline) {
    if (token !== activeToken || isDetached(ui.$wrap)) return;

    try {
      const r = await fetch(`/meta_apis/ad_sets/${adSetId}/status`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!r.ok) throw new Error(`status check failed: ${r.status}`);
      const body = await r.json() as StatusBody;
      if (token !== activeToken) return;

      consecutiveErrors = 0;
      lastStatus = body.status;

      if (body.status === 'created') {
        applyState(ui, 'is-success', ICON_SUCCESS);
        setBar(ui, 100, true);
        ui.$label.text('Ad set created on Meta').removeAttr('title');
        await delay(1200);
        if (token !== activeToken) return;
        const facebook_adset_id = body.facebook_adset_id;
        if (facebook_adset_id) {
          const params = new URLSearchParams({ selected_adset_ids: facebook_adset_id });
          if (body.campaign_id) params.set('selected_campaign_ids', body.campaign_id);
          window.location.href = `/meta_apis/ad_sets/${encodeURIComponent(facebook_adset_id)}?${params.toString()}`;
        } else {
          window.location.href = '/meta_apis/ad_sets?refresh=1';
        }
        return;
      }
      if (body.status === 'failed') {
        const msg = body.error_message || 'Ad set creation failed on Meta.';
        renderTerminal(ui, 'is-error', ICON_ERROR, `Failed: ${msg}`, msg);
        return;
      }

      ui.$label.text(body.status === 'pending' ? 'Queued…' : 'Processing on Meta…');
      creep = Math.min(CREEP_CAP, creep + CREEP_STEP);
      setBar(ui, creep, true);
    } catch {
      if (token !== activeToken) return;
      consecutiveErrors++;
      if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
        const msg =
          `Lost connection while checking ad set status (${MAX_CONSECUTIVE_ERRORS} consecutive errors). ` +
          'The ad set may still be processing on Meta — refresh the ad set list to confirm.';
        renderTerminal(ui, 'is-warning', ICON_WARNING, 'Lost connection — refresh to confirm.', msg);
        return;
      }
    }

    await delay(POLL_INTERVAL_MS);
  }

  if (token !== activeToken) return;
  const stalledMessage = (lastStatus === 'processing' || lastStatus === 'pending')
    ? `Ad set has been "${lastStatus}" for over 5 minutes and may have stalled. ` +
      'Refresh the ad set list — if the ad set is missing, try creating it again.'
    : 'Ad set status check timed out after 5 minutes. Refresh the ad set list to see the outcome.';
  renderTerminal(ui, 'is-warning', ICON_WARNING, 'Timed out — refresh to see the outcome.', stalledMessage);
}

function renderTerminal(
  ui: ProgressUI,
  stateClass: 'is-error' | 'is-warning',
  iconHtml: string,
  shortText: string,
  fullText: string,
): void {
  applyState(ui, stateClass, iconHtml);
  setBar(ui, 100, false);
  ui.$label.text(shortText).attr('title', fullText);
}

function applyState(ui: ProgressUI, stateClass: string, iconHtml: string): void {
  ui.$wrap.removeClass('is-active is-success is-error is-warning').addClass(stateClass);
  ui.$fill.removeClass('progress-bar-striped progress-bar-animated');
  ui.$icon.html(iconHtml);
}

function setBar(ui: ProgressUI, pct: number, showPercent: boolean): void {
  const v = Math.max(0, Math.min(100, Math.round(pct)));
  ui.$fill.css('width', `${v}%`).attr('aria-valuenow', String(v));
  ui.$percent.text(showPercent ? `${v}%` : '');
}

function hideProgress($wrap: JQuery): void {
  $wrap.addClass('d-none').removeClass('is-active is-success is-error is-warning');
}

function isDetached($wrap: JQuery): boolean {
  const el = $wrap.get(0);
  return !el || !document.body.contains(el);
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
